import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AppSettings } from '../app-settings';
import {
  WorkflowTaskSummary,
  WorkflowTaskDetail,
  WorkflowPacketDetail,
  WorkflowAttachment,
  WorkflowCaseIndex,
  ActiveWorkflowCase,
  LandingReviewItem
} from '../domain/types/workflow';

@Injectable({ providedIn: 'root' })
export class WorkflowService {
  private baseUrl: string;

  constructor(private http: HttpClient, private appSettings: AppSettings) {
    this.baseUrl = `${appSettings.APIURL}/workflow`;
  }

  // ─── Lookups ──────────────────────────────────────────────

  async getAcademics(): Promise<{ id: string; name: string }[]> {
    return firstValueFrom(
      this.http.get<{ id: string; name: string }[]>(`${this.baseUrl}/academics`)
    );
  }

  // ─── My Cases ────────────────────────────────────────────

  async getMyCases(): Promise<WorkflowCaseIndex[]> {
    return firstValueFrom(
      this.http.get<WorkflowCaseIndex[]>(`${this.baseUrl}/my-cases`)
    );
  }

  // ─── Admin ───────────────────────────────────────────────

  async getAllCases(): Promise<WorkflowCaseIndex[]> {
    return firstValueFrom(
      this.http.get<WorkflowCaseIndex[]>(`${this.baseUrl}/all-cases`)
    );
  }

  // ─── Packets ───────────────────────────────────────────────

  async getPacketByAcademic(academicId: string): Promise<{ caseId: number }> {
    return firstValueFrom(
      this.http.get<{ caseId: number }>(`${this.baseUrl}/packets/by-academic/${academicId}`)
    );
  }

  async getActivePacketsByAcademic(academicId: string): Promise<ActiveWorkflowCase[]> {
    return firstValueFrom(
      this.http.get<ActiveWorkflowCase[]>(`${this.baseUrl}/packets/by-academic/${academicId}/active`)
    );
  }

  async getPacket(caseId: number): Promise<WorkflowPacketDetail> {
    return firstValueFrom(
      this.http.get<WorkflowPacketDetail>(`${this.baseUrl}/packets/${caseId}`)
    );
  }

  async retryStalledCase(caseId: number): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(`${this.baseUrl}/packets/${caseId}/retry`, {})
    );
  }

  /** Toggle the "admin assignment" flag — grants the candidate +1 page on the Program Summary Narrative. */
  async setAdminAssignment(caseId: number, value: boolean): Promise<{ hasAdminAssignment: boolean }> {
    return firstValueFrom(
      this.http.post<{ hasAdminAssignment: boolean }>(
        `${this.baseUrl}/packets/${caseId}/admin-assignment`,
        { value }
      )
    );
  }

  // ─── Tasks ─────────────────────────────────────────────────

  async getMyTasks(includeCompleted = false): Promise<WorkflowTaskSummary[]> {
    const params = includeCompleted ? '?includeCompleted=true' : '';
    return firstValueFrom(
      this.http.get<WorkflowTaskSummary[]>(`${this.baseUrl}/my-tasks${params}`)
    );
  }

  /** Read-only reviewer roles (e.g. secondary PRC) — no workflow.Task, so they
   *  aren't in getMyTasks. Used by the landing page. */
  async getReadOnlyReviews(): Promise<LandingReviewItem[]> {
    return firstValueFrom(
      this.http.get<LandingReviewItem[]>(`${this.baseUrl}/landing-readonly-reviews`)
    );
  }

  async getTask(taskId: number): Promise<WorkflowTaskDetail> {
    return firstValueFrom(
      this.http.get<WorkflowTaskDetail>(`${this.baseUrl}/tasks/${taskId}`)
    );
  }

  async completeTask(taskId: number, formData: Record<string, any>): Promise<void> {
    await firstValueFrom(
      this.http.post(`${this.baseUrl}/tasks/${taskId}/complete`, { formData })
    );
  }

  async rescindTask(taskId: number): Promise<void> {
    await firstValueFrom(
      this.http.post(`${this.baseUrl}/tasks/${taskId}/rescind`, {})
    );
  }

  // ─── Files ─────────────────────────────────────────────────

  async uploadAttachment(taskId: number, file: File, documentSlot: string): Promise<WorkflowAttachment> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentSlot', documentSlot);
    return firstValueFrom(
      this.http.post<WorkflowAttachment>(`${this.baseUrl}/tasks/${taskId}/attachments`, formData)
    );
  }

  async deleteAttachment(attachmentId: number): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.baseUrl}/attachments/${attachmentId}`)
    );
  }

  private fetchAttachmentBlob(attachmentId: number): Promise<HttpResponse<Blob>> {
    return firstValueFrom(
      this.http.get(`${this.baseUrl}/attachments/${attachmentId}/download`, {
        responseType: 'blob',
        observe: 'response'
      })
    );
  }

  viewAttachment(attachmentId: number): void {
    this.fetchAttachmentBlob(attachmentId).then(response => {
      const blob = response.body;
      if (!blob) return;
      const contentType = response.headers.get('Content-Type') || 'application/octet-stream';
      const url = window.URL.createObjectURL(new Blob([blob], { type: contentType }));
      window.open(url, '_blank');
    });
  }

  downloadAttachment(attachmentId: number, fallbackFileName: string): void {
    this.fetchAttachmentBlob(attachmentId).then(response => {
      const blob = response.body;
      if (!blob) return;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fallbackFileName;
      a.click();
      window.URL.revokeObjectURL(url);
    });
  }
}
